import { ChangeDetectorRef, Component, DoCheck, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AgentIaState } from './agent-ia.state';
import { AgentActivity, AgentPhase, elapsedLabel } from './agent-ia.models';
import { AgentFollowService } from './agent-follow.service';
import { AgentFollower, AgentNavIntent, announceText } from './agent-follow';
import { AgentFollowCardComponent } from './agent-follow-card.component';

// Activity bubble of the follow mode (SPEC §8, follow mode): « L’Agent IA pilote Aglo », what the run does now, its last
// change, the follow state and the pending navigation. Mounted by AgentIaComponent (outside the routed workspace);
// positioned with classes only. Phases of an active run stay visible; « Terminé » / « Arrêté » 15 s; a failure until closed.
// When it shows nothing, the card « Claude modifie Aglo » of the DPGF activity feed takes its place (docs/mcp/06-FRONT.md §8.3).
const ACTIVE_PHASES: AgentPhase[] = ['starting', 'analysing', 'working', 'thinking', 'stopping'];
const SLOW_PHASES: AgentPhase[] = ['analysing', 'working', 'thinking'];
const DISMISSABLE: AgentPhase[] = ['waiting', 'approval', 'done', 'failed', 'cancelled'];
const SHOWN_MS = 15000, SLOW_MS = 120000;

@Component({
  selector: 'app-agent-activity-bubble', standalone: true, imports: [CommonModule, AgentFollowCardComponent],
  templateUrl: './agent-activity-bubble.component.html', styleUrls: ['./agent-activity-bubble.component.scss']
})
export class AgentActivityBubbleComponent implements OnInit, DoCheck, OnDestroy {
  @Input({ required: true }) state!: AgentIaState;
  @Input() panelOpen = false;
  @Input() mobile = false;
  @Output() openPanel = new EventEmitter<void>();
  readonly follow: AgentFollower;
  readonly announceText = announceText;
  clock = Date.now();
  covered = false;
  private collapsedRun: string | null = null;
  private dismissed = '';
  private ticker: ReturnType<typeof setInterval> | null = null;
  private subscription: Subscription | null = null;
  private destroyed = false;

  constructor(private followService: AgentFollowService, private router: Router, private cd: ChangeDetectorRef, private zone: NgZone) {
    this.follow = followService.engine;
  }
  ngOnInit() {
    this.subscription = this.followService.viewChanged$.subscribe(() => this.zone.run(() => this.cd.markForCheck()));
    // Elapsed time, end of « Terminé », modal covering the page: checked every second without app-wide change detection.
    this.zone.runOutsideAngular(() => { this.ticker = setInterval(() => this.tick(), 1000); });
  }
  ngDoCheck() { this.clock = Date.now(); this.covered = this.followService.modalOpen(); }
  ngOnDestroy() { this.destroyed = true; if (this.ticker) clearInterval(this.ticker); this.subscription?.unsubscribe(); }
  private tick() {
    if (this.destroyed) return;
    const before = this.viewKey();
    this.clock = Date.now(); this.covered = this.followService.modalOpen();
    if (this.viewKey() !== before) this.cd.detectChanges();
  }
  private viewKey() { const activity = this.activity; return [this.visible, this.covered, activity ? this.elapsed(activity) : '', this.slow].join('|'); }

  get activity(): AgentActivity | null { return this.state.activity; }
  get visible() {
    const activity = this.activity;
    if (!activity || this.covered || (this.mobile && this.panelOpen) || this.dismissed === `${activity.runId}|${activity.phase}|${activity.since}`) return false;
    return !['done', 'cancelled'].includes(activity.phase) || this.clock - activity.since < SHOWN_MS;
  }
  get active() { const phase = this.activity?.phase; return Boolean(phase && ACTIVE_PHASES.includes(phase)); }
  get collapsed() { return this.collapsedRun !== null && this.collapsedRun === this.activity?.runId; }
  get slow() { const activity = this.activity; return Boolean(activity && SLOW_PHASES.includes(activity.phase) && this.clock - activity.since > SLOW_MS); }
  get dismissable() { const phase = this.activity?.phase; return Boolean(phase && DISMISSABLE.includes(phase)); }
  get raised() { return /^\/architect\/dpgf\/[^/?#]+\/result(?:[/?#]|$)/.test(this.router.url); }
  get owned() { return this.state.ownsRun; }
  // The run is followed from another tab or device: this one only shows it (« Suivre ici »).
  get otherTab() { return this.active && !this.owned; }
  get projectName() { return this.state.conversation?.projectTitle || ''; }
  // Pending navigation blocked by the page (unsaved edits, CCTP editor…): « <reason>. L’IA travaille sur le lot X. »
  holdMessage(intent: AgentNavIntent) {
    const reason = this.follow.holdText.replace(/[\s.!…]+$/, '');
    return `${reason ? `${reason}. ` : ''}L’IA travaille sur ${intent.label}.`;
  }
  offerLabel(intent: AgentNavIntent) { return intent.kind === 'plan' ? 'Voir le plan' : intent.kind === 'dpgf' ? 'Voir dans la DPGF' : intent.kind === 'library' ? 'Voir la bibliothèque' : 'Voir'; }
  elapsed(activity: AgentActivity) { return ACTIVE_PHASES.includes(activity.phase) ? elapsedLabel(this.clock - activity.since) : ''; }
  primaryLabel(phase: AgentPhase) {
    switch (phase) {
      case 'waiting': return 'Répondre';
      case 'approval': return 'Voir l’action';
      case 'done': return 'Voir le résumé';
      case 'failed': return 'Voir le détail';
      default: return 'Ouvrir la conversation';
    }
  }

  collapse() { this.collapsedRun = this.activity?.runId ?? null; }
  expand() { this.collapsedRun = null; }
  dismiss() { const activity = this.activity; if (activity) this.dismissed = `${activity.runId}|${activity.phase}|${activity.since}`; }
  toggleFollow() { this.follow.following ? this.follow.pause() : this.follow.resume(); }
  followHere() { this.state.adoptRun(); this.follow.resume(); }
  stay() { this.follow.pause(); }
  go(intent: AgentNavIntent) { void this.follow.open(intent); }
}
